import React, { useLayoutEffect, useRef, useState } from "react";

import {
  DISTRICTS,
  GROWTH_STAGES,
  districtProgress,
  growthStage,
  plantJitter,
} from "../../lib/tokiPonaGarden";
import PlantMark from "./PlantMark";

/* ============================================================
   kon nimi — the garden map. Nine semantic districts, each a
   patch of plants (words) that grow as the learner journals.
   Learner-drawn vines connect words across districts.
   ============================================================ */

const GardenMap = ({ entries, learned, links = [], todayWord, onOpenWord }) => {
  const plotsRef = useRef(null);
  const [vines, setVines] = useState([]);

  useLayoutEffect(() => {
    const measure = () => {
      const container = plotsRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const next = [];
      for (const link of links) {
        const a = container.querySelector(`[data-word="${link.from}"]`);
        const b = container.querySelector(`[data-word="${link.to}"]`);
        if (!a || !b) continue;
        const ar = a.getBoundingClientRect();
        const br = b.getBoundingClientRect();
        const ax = ar.left + ar.width / 2 - rect.left;
        const ay = ar.top + ar.height / 2 - rect.top;
        const bx = br.left + br.width / 2 - rect.left;
        const by = br.top + br.height / 2 - rect.top;
        const mx = (ax + bx) / 2;
        const my = (ay + by) / 2;
        const dx = bx - ax;
        const dy = by - ay;
        const len = Math.hypot(dx, dy) || 1;
        const px = (-dy / len) * 24;
        const py = (dx / len) * 24;
        next.push({
          key: `${link.from}·${link.to}·${link.at}`,
          d: `M ${ax} ${ay} Q ${mx + px} ${my + py} ${bx} ${by}`,
        });
      }
      setVines(next);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [links]);

  return (
    <div className="kn-garden">
      <div className="kn-hero">
        <h1>kon nimi</h1>
        <p className="kn-hero-sub">
          the spirit of the words — a field guide you write yourself
        </p>
        <p className="kn-hero-copy">
          Every word here is a plant. Answer its questions and it grows — soil,
          seed, sprout, leaf, flower — one small section at a time. There is no
          score and no clock; a word can sit in the soil for months and still
          be exactly where you left it.
        </p>
        <div className="kn-legend">
          {GROWTH_STAGES.map((stage, i) => (
            <span className={`kn-legend-item st-${i}`} key={stage}>
              <PlantMark stage={i} />
              {stage}
            </span>
          ))}
        </div>
        {links.length > 0 && (
          <p className="kn-hero-vines">
            {links.length} vine{links.length === 1 ? "" : "s"} tied
          </p>
        )}
        {todayWord && (
          <button
            type="button"
            className="kn-hero-today"
            onClick={() => onOpenWord(todayWord)}
          >
            nimi pi tenpo suno ni · today&apos;s word is <b>{todayWord}</b> — pay
            it a visit
          </button>
        )}
      </div>

      <div className="kn-garden-plots" ref={plotsRef}>
        <svg className="kn-vines" aria-hidden="true">
          {vines.map((v) => (
            <path key={v.key} d={v.d} className="kn-vine" />
          ))}
        </svg>

        {DISTRICTS.map((district) => {
          const { grown, total } = districtProgress(district, entries);
          return (
            <div className="kn-district" key={district.id}>
              <div className="kn-district-head">
                <div>
                  <span className="kn-district-name">{district.name}</span>
                  <span className="kn-district-gloss">{district.gloss}</span>
                </div>
                <span className="kn-district-count">
                  {grown === 0 ? "nothing planted yet" : `${grown} of ${total} growing`}
                </span>
              </div>
              <p className="kn-district-note">{district.note}</p>
              <div className="kn-plants">
                {district.words.map((word) => {
                  const stage = growthStage(entries[word] || {});
                  const { dx, dy, rot } = plantJitter(word);
                  const classes = `kn-plant st-${stage}${learned.has(word) ? " sun" : ""}${
                    word === todayWord ? " today" : ""
                  }`;
                  return (
                    <button
                      key={word}
                      type="button"
                      className={classes}
                      data-word={word}
                      style={{ transform: `translate(${dx}px, ${dy}px) rotate(${rot}deg)` }}
                      onClick={() => onOpenWord(word)}
                    >
                      <PlantMark stage={stage} />
                      <span className="kn-plant-name">{word}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GardenMap;
